/**
 * TimelineRenderer — timeline
 * Horizontal or vertical step-by-step progression.
 * orientation auto-switches to vertical when ≥ 4 steps or explicitly set.
 */
import React from "react";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";
import { SortableList } from "../SortableList";

export function TimelineRenderer({
  element,
  editing,
  onChange,
  onReorder,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const steps = element.steps || [];
  const title = element.title ? resolveText(element.title) : null;
  const titleBox = element.title?.boxAnimation ?? null;
  const titleExit = element.title?.boxExitAnimation ?? null;
  const orientation = element.orientation === "vertical" ? "vertical" : "horizontal";

  const gridStyle =
    orientation === "horizontal"
      ? {
          gridTemplateColumns: `repeat(${steps.length || 1}, minmax(0, 1fr))`,
          alignItems: "stretch",
        }
      : { gridTemplateColumns: "1fr" };

  return (
    <SurfaceCard compact>
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {title && (
          <AnimatedField
            boxAnimation={titleBox}
            boxExitAnimation={titleExit}
            playing={playing}
            isPaused={isPaused}
            currentTimeSec={currentTimeSec}
          >
            <Title size="xl">
              <EditableText
                value={title}
                editing={editing}
                onChange={(v) => onChange?.("title", v)}
              />
            </Title>
          </AnimatedField>
        )}

        {/* Progress track */}
        <div
          style={{
            height: "3px",
            background: "var(--color-bg-surface-strong)",
            borderRadius: "var(--radius-full)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: "100%",
              background: "var(--color-text-accent)",
              borderRadius: "var(--radius-full)",
            }}
          />
        </div>

        {/* Steps */}
        <div style={{ display: "grid", gap: "var(--space-4)", ...gridStyle }}>
          <SortableList
            editing={editing}
            direction={orientation}
            items={steps}
            onReorder={(newSteps) => onReorder?.("steps", newSteps)}
            renderItem={(step, i) => {
              const stepTitle = resolveText(step.title);
              const body = resolveTextOptional(step.body);
              // Prefer title-level animation; fall back to body animation
              const stepBox =
                step.title?.boxAnimation ?? step.body?.boxAnimation ?? step.boxAnimation ?? null;
              const stepExit =
                step.title?.boxExitAnimation ??
                step.body?.boxExitAnimation ??
                step.boxExitAnimation ??
                null;

              return (
                <div style={{ position: "relative", height: "100%" }}>
                  <AnimatedField
                    boxAnimation={stepBox}
                    boxExitAnimation={stepExit}
                    playing={playing}
                    isPaused={isPaused}
                    currentTimeSec={currentTimeSec}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--space-2)",
                        padding: "var(--surface-padding-compact)",
                        borderRadius: "var(--surface-radius)",
                        border: "var(--stroke-thin) solid var(--color-border-subtle)",
                        background: "var(--color-bg-surface-strong)",
                        height: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: "var(--text-sm)",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "var(--color-text-accent)",
                          fontWeight: 600,
                        }}
                      >
                        Step {i + 1}
                      </span>
                      <Title size="lg">
                        <EditableText
                          value={stepTitle}
                          editing={editing}
                          onChange={(v) => onChange?.(`steps.${i}.title`, v)}
                        />
                      </Title>
                      {(body !== null || editing) && (
                        <Text role="caption">
                          <EditableText
                            value={body ?? ""}
                            editing={editing}
                            onChange={(v) => onChange?.(`steps.${i}.body`, v)}
                            placeholder="• Add description…"
                          />
                        </Text>
                      )}
                    </div>
                  </AnimatedField>
                  {editing && (
                    <button
                      type="button"
                      aria-label="Delete step"
                      onClick={() =>
                        onReorder?.(
                          "steps",
                          steps.filter((_, idx) => idx !== i)
                        )
                      }
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "none",
                        borderRadius: "var(--radius-sm, 4px)",
                        background: "transparent",
                        color: "var(--color-text-muted, #94a3b8)",
                        cursor: "pointer",
                        fontSize: 16,
                        lineHeight: 1,
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            }}
          />
        </div>
        {editing && (
          <button
            type="button"
            aria-label="Add step"
            onClick={() =>
              onReorder?.("steps", [...steps, { title: "New step", body: "Description" }])
            }
            style={{
              width: "100%",
              padding: "6px",
              border: "1px dashed var(--color-border-subtle, rgba(255,255,255,0.12))",
              borderRadius: "var(--radius-sm, 4px)",
              background: "transparent",
              color: "var(--color-text-muted, #94a3b8)",
              cursor: "pointer",
              fontSize: "var(--text-sm, 13px)",
              fontFamily: "var(--font-body, sans-serif)",
              textAlign: "center",
            }}
          >
            + Add step
          </button>
        )}
      </div>
    </SurfaceCard>
  );
}

export default TimelineRenderer;
